import api from './axiosConfig';

export type PortfolioThresholdItem = {
  ticker: string;
  tickerName: string;
  upPct: number;
  downPct: number;
  enabled: boolean;
};

export type PortfolioThresholdSettings = {
  userId: number;
  holdingCount: number;
  defaultUpPct: number;
  defaultDownPct: number;
  items: PortfolioThresholdItem[];
};

export type PortfolioThresholdUpdate = {
  ticker: string;
  upPct: number;
  downPct: number;
  enabled: boolean;
};

export type TelegramRealtimeStatus = {
  linked?: boolean;
  pending?: boolean;
  realtimeAlertsActive?: boolean;
  morningBriefActive?: boolean;
  deepLink?: string | null;
  expiresAt?: string | null;
  message?: string | null;
};

export async function getPortfolioThresholdSettings(): Promise<PortfolioThresholdSettings> {
  const response = await api.get('/alerts/portfolio/settings');
  const data = response?.data ?? {};
  const items = Array.isArray(data.items) ? data.items : [];

  return {
    userId: Number(data.userId ?? 0),
    holdingCount: Number(data.holdingCount ?? items.length ?? 0),
    defaultUpPct: Number(data.defaultUpPct ?? 5),
    defaultDownPct: Number(data.defaultDownPct ?? -4),
    items: items
      .map((item: any) => ({
        ticker: String(item?.ticker ?? '').trim(),
        tickerName: String(item?.tickerName ?? item?.ticker ?? '').trim(),
        upPct: Math.abs(Number(item?.upPct ?? data.defaultUpPct ?? 5)),
        downPct: -Math.abs(Number(item?.downPct ?? data.defaultDownPct ?? -4)),
        enabled: item?.enabled !== false,
      }))
      .filter((item: PortfolioThresholdItem) => item.ticker.length > 0),
  };
}

export async function updatePortfolioThresholdSettings(
  items: PortfolioThresholdUpdate[],
): Promise<PortfolioThresholdSettings> {
  const response = await api.put('/alerts/portfolio/settings', { items });
  return normalizeSettings(response?.data ?? {});
}

export async function updateAllPortfolioThresholdSettings(payload: {
  upPct: number;
  downPct: number;
  enabled?: boolean;
}): Promise<PortfolioThresholdSettings> {
  const response = await api.put('/alerts/portfolio/settings', {
    upPct: Math.abs(Number(payload.upPct)),
    downPct: -Math.abs(Number(payload.downPct)),
    enabled: payload.enabled !== false,
  });
  return normalizeSettings(response?.data ?? {});
}

export async function getTelegramRealtimeStatus(): Promise<TelegramRealtimeStatus> {
  const response = await api.get('/alerts/telegram/connect/status');
  return (response?.data ?? {}) as TelegramRealtimeStatus;
}

export async function setTelegramRealtimeActive(active: boolean): Promise<TelegramRealtimeStatus> {
  const response = await api.post('/alerts/telegram/realtime/subscription', { active });
  return (response?.data ?? {}) as TelegramRealtimeStatus;
}

export async function beginTelegramRealtimeConnection(): Promise<TelegramRealtimeStatus> {
  const response = await api.post('/alerts/telegram/connect', {
    subscribeMorningBrief: false,
  });
  return (response?.data ?? {}) as TelegramRealtimeStatus;
}

function normalizeSettings(data: any): PortfolioThresholdSettings {
  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    userId: Number(data?.userId ?? 0),
    holdingCount: Number(data?.holdingCount ?? items.length ?? 0),
    defaultUpPct: Number(data?.defaultUpPct ?? 5),
    defaultDownPct: Number(data?.defaultDownPct ?? -4),
    items: items
      .map((item: any) => ({
        ticker: String(item?.ticker ?? '').trim(),
        tickerName: String(item?.tickerName ?? item?.ticker ?? '').trim(),
        upPct: Math.abs(Number(item?.upPct ?? data?.defaultUpPct ?? 5)),
        downPct: -Math.abs(Number(item?.downPct ?? data?.defaultDownPct ?? -4)),
        enabled: item?.enabled !== false,
      }))
      .filter((item: PortfolioThresholdItem) => item.ticker.length > 0),
  };
}
